/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FooterData } from "../../types/components/datatable";
import { Dropdown } from "react-bootstrap";
import { ConfirmModal } from "../Modal/ConfirmModal";

export const PAGE_SIZE_OPTION = [10, 15, 25, 50, 100];

export const TableFooter: FC<FooterData> = ({
  offset,
  numberOfElements,
  totalElements,
  first,
  last,
  searchPage,
  pageNumber,
  loading,
  className = "",
  asyncTotalCount = 0,
  showClickableTotalCount = false,
  showSelectablePageSize = false,
  pageSize = 10,
  pageSizeOptions = PAGE_SIZE_OPTION,
  onPageSizeChange,
  getTotalCount,
  isDark = false,
  countFilter = "default",
}) => {
  const { t } = useTranslation("table");
  const [currentPage, setCurrentPage] = useState<number>(pageNumber);
  const [showCountConfirmModal, setShowCountConfirmModal] =
    useState<boolean>(false);

  const handlePageSizeChange = (size: number) => {
    onPageSizeChange?.(size);
    setCurrentPage(0);
    searchPage({ page: 0, size });
  };

  const goToNext = useCallback(() => {
    setCurrentPage(pageNumber + 1);
    searchPage({ page: pageNumber + 1 });
  }, [pageNumber, searchPage]);

  const goToPrev = useCallback(() => {
    setCurrentPage(pageNumber - 1);
    searchPage({ page: pageNumber - 1 });
  }, [pageNumber, searchPage]);

  useEffect(() => {
    if (
      currentPage >= pageNumber &&
      numberOfElements === 0 &&
      last &&
      !first &&
      !loading
    ) {
      goToPrev();
    }
  }, [
    goToPrev,
    last,
    numberOfElements,
    currentPage,
    pageNumber,
    loading,
    first,
  ]);

  const hasAnyFilter = () => {
    if (countFilter === "default") return true;
    if (!countFilter) return false;
    return Object.entries(countFilter).some(([key, value]) => {
      if (
        key === "isFuzzySearch" ||
        key === "sortKeys" ||
        key === "page" ||
        key === "size"
      )
        return false;
      if (value === undefined || value === null) return false;

      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "string") return value.trim() !== "";
      if (value instanceof Date) return !isNaN(value.getTime());

      return true;
    });
  };

  return (
    <>
      {showCountConfirmModal && (
        <ConfirmModal
          show={showCountConfirmModal}
          cancel={() => {
            setShowCountConfirmModal(false);
          }}
          body={
            <>
              <div className="text-center">{t("confirm_count_message")}</div>
            </>
          }
          confirmBtnName={t("ok")}
          confirm={() => {
            getTotalCount();
            setShowCountConfirmModal(false);
          }}
          cancelBtnName={t("cancel")}
        />
      )}

      <div
        className={`card-footer bg-white d-sm-flex justify-content-sm-between align-items-sm-center ${className}`}
      >
        <div className="d-flex align-items-center">
          <span id="resultsTotal">
            {totalElements
              ? t("paging.showing", {
                  start: totalElements > 0 ? offset + 1 : 0,
                  end:
                    offset + numberOfElements > totalElements
                      ? totalElements
                      : offset + numberOfElements || 0,
                  total: totalElements,
                })
              : t("paging.showing_without_total", {
                  start: numberOfElements > 0 ? offset + 1 : 0,
                  end: offset + numberOfElements || 0,
                })}
          </span>
          {showClickableTotalCount &&
            getTotalCount &&
            (asyncTotalCount ? (
              <span className="ms-2 ml-1">{` ${t(
                "of"
              )} ${asyncTotalCount}`}</span>
            ) : (
              <>
                <span className="ms-2 ml-1">{t("of")}</span>
                <span
                  className="m-l-xxs cursor-pointer btn-link"
                  onClick={() => {
                    if (!hasAnyFilter()) setShowCountConfirmModal(true);
                    else getTotalCount();
                  }}
                >
                  {t("paging.count")}
                </span>
              </>
            ))}
        </div>
        <div className="d-flex align-items-center">
          {showSelectablePageSize && (
            <Dropdown
              className={`m-r-md nav-item dropdown custom-dropdown ${
                isDark ? "dark" : ""
              }`}
            >
              <Dropdown.Toggle variant="light" size="sm">
                {t("paging.size")}: {pageSize}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {pageSizeOptions.map((size) => (
                  <Dropdown.Item
                    key={size}
                    onClick={() => {
                      handlePageSizeChange(size);
                    }}
                    active={pageSize === size}
                  >
                    {size}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
          <ul className="pagination pagination-sm pagination-pager justify-content-between mt-2 mt-sm-0">
            <li
              className={`page-item ${
                first || totalElements === 0 ? "disabled" : ""
              }`}
            >
              <button type="button" onClick={goToPrev} className="page-link">
                ← &nbsp; {t("paging.previous")}
              </button>
            </li>
            <li
              className={`page-item ${
                last || totalElements === 0 ? "disabled" : ""
              }`}
            >
              <button type="button" className="page-link" onClick={goToNext}>
                {t("paging.next")} &nbsp; →
              </button>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};
